<div class="integration-type-management">
  <!-- Header with buttons -->
  <div class="d-flex justify-content-between align-items-center mb-3">
    <h5 class="mb-0">
      <i class="bi bi-plug me-2"></i>
      {{ 'SystemAdmin.integrationTypeManagement' | translate }}
    </h5>
    
    <button type="button" class="btn btn-primary btn-sm" (click)="openCreateModal()">
      <i class="bi bi-plus-circle me-1"></i>
      {{ 'SystemAdmin.addIntegrationType' | translate }}
    </button>
  </div>

  <!-- Smart Grid -->
  <div class="row" *ngIf="columns.length > 0">
    <div class="col-12">
      <konti-smart-grid 
        [data]="integrationTypes?.rows || []" 
        [columns]="columns" 
        [initialSort]="[{ field: 'typeName', dir: 'asc' }]"
        [showGroupPanel]="false">
        
        <!-- Category badge template -->
        <ng-template appSmartGridCell="categoryBadge" let-row>
          <span class="badge" [ngClass]="getCategoryClass(row.category)">
            {{ row.category }}
          </span>
        </ng-template>

        <!-- Active badge template -->
        <ng-template appSmartGridCell="activeBadge" let-row>
          <span class="badge" [ngClass]="row.isActive ? 'badge-soft-success' : 'badge-soft-secondary'">
            {{ (row.isActive ? 'General.active' : 'General.inactive') | translate }}
          </span>
        </ng-template>
        
        <!-- Actions column -->
        <ng-template appSmartGridCell="actions" let-row>
          <div class="action-capsule btn-group btn-group-sm sg-desktop-only">
            <button type="button" class="btn btn-outline-secondary" 
                    (click)="openEditModal(row)"
                    [title]="'General.edit' | translate">
              <i class="bi bi-pencil" aria-hidden="true"></i>
              <span class="visually-hidden">{{ 'General.edit' | translate }}</span>
            </button>
            <button type="button" class="btn btn-outline-danger action-danger" 
                    (click)="openDeleteModal(row)"
                    [title]="'General.delete' | translate">
              <i class="bi bi-trash3" aria-hidden="true"></i>
              <span class="visually-hidden">{{ 'General.delete' | translate }}</span>
            </button>
          </div>
        </ng-template>
      </konti-smart-grid>
    </div>
  </div>
</div>

<!-- Integration Type Modal (Create/Edit) -->
<ng-template #integrationTypeModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-plug me-2"></i>
      {{ (isEditing ? 'SystemAdmin.editIntegrationType' : 'SystemAdmin.addIntegrationType') | translate }}
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
  </div>
  
  <div class="modal-body">
    <form [formGroup]="integrationTypeForm">
      <!-- Type Code -->
      <div class="mb-3">
        <label class="form-label">
          {{ 'SystemAdmin.typeCode' | translate }} <span class="text-danger">*</span>
        </label>
        <input type="text" class="form-control font-monospace" formControlName="typeCode" maxlength="50"
               placeholder="ABAX, TRIPLETEX, MSSQL, CSV"
               [class.is-invalid]="f['typeCode'].invalid && f['typeCode'].touched"
               [readonly]="isEditing" />
        <small class="text-muted">{{ 'SystemAdmin.typeCodeHint' | translate }}</small>
        <div class="invalid-feedback" *ngIf="f['typeCode'].invalid && f['typeCode'].touched">
          <span *ngIf="f['typeCode'].errors?.['required']">{{ 'General.required' | translate }}</span>
          <span *ngIf="f['typeCode'].errors?.['pattern']">{{ 'SystemAdmin.typeCodePatternError' | translate }}</span>
        </div>
      </div>

      <!-- Type Name -->
      <div class="mb-3">
        <label class="form-label">
          {{ 'SystemAdmin.typeName' | translate }} <span class="text-danger">*</span>
        </label>
        <input type="text" class="form-control" formControlName="typeName" maxlength="100"
               placeholder="Abax Integration"
               [class.is-invalid]="f['typeName'].invalid && f['typeName'].touched" />
        <div class="invalid-feedback" *ngIf="f['typeName'].invalid && f['typeName'].touched">
          {{ 'General.required' | translate }}
        </div>
      </div>

      <!-- Category -->
      <div class="mb-3">
        <label class="form-label">
          {{ 'SystemAdmin.category' | translate }} <span class="text-danger">*</span>
        </label>
        <select class="form-select" formControlName="category"
                [class.is-invalid]="f['category'].invalid && f['category'].touched">
          <option *ngFor="let cat of categories" [value]="cat.value">
            {{ 'SystemAdmin.' + cat.value.toLowerCase() | translate }}
          </option>
        </select>
        <div class="invalid-feedback" *ngIf="f['category'].invalid && f['category'].touched">
          {{ 'General.required' | translate }}
        </div>
      </div>

      <!-- Adapter Class Name -->
      <div class="mb-3">
        <label class="form-label">
          {{ 'SystemAdmin.adapterClassName' | translate }} <span class="text-danger">*</span>
        </label>
        <input type="text" class="form-control font-monospace" formControlName="adapterClassName" maxlength="200"
               placeholder="ePortal.API.Services.Integration.AbaxIntegrationAdapter"
               [class.is-invalid]="f['adapterClassName'].invalid && f['adapterClassName'].touched" />
        <small class="text-muted">{{ 'SystemAdmin.adapterClassNameHint' | translate }}</small>
        <div class="invalid-feedback" *ngIf="f['adapterClassName'].invalid && f['adapterClassName'].touched">
          {{ 'General.required' | translate }}
        </div>
      </div>

      <!-- Description -->
      <div class="mb-3">
        <label class="form-label">{{ 'General.description' | translate }}</label>
        <textarea class="form-control" formControlName="description" rows="2" maxlength="500"
                  [class.is-invalid]="f['description'].invalid && f['description'].touched"></textarea>
        <div class="invalid-feedback" *ngIf="f['description'].invalid && f['description'].touched">
          {{ 'SystemAdmin.descriptionTooLong' | translate }}
        </div>
      </div>

      <!-- Config Schema (JSON) -->
      <div class="mb-3">
        <label class="form-label">{{ 'SystemAdmin.configSchema' | translate }}</label>
        <textarea class="form-control font-monospace" formControlName="configSchema" rows="4"
                  placeholder='{"apiEndpoint": "string", "apiKey": "string", "timeout": "int"}'></textarea>
        <small class="text-muted">{{ 'SystemAdmin.configSchemaHint' | translate }}</small>
      </div>

      <!-- Feature Flags -->
      <h6 class="border-bottom pb-2 mb-3">{{ 'SystemAdmin.features' | translate }}</h6>
      
      <div class="row">
        <div class="col-md-6">
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" formControlName="requiresAuthentication" id="requiresAuth">
            <label class="form-check-label" for="requiresAuth">
              {{ 'SystemAdmin.requiresAuthentication' | translate }}
            </label>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" formControlName="supportsBatchProcessing" id="supportsBatch">
            <label class="form-check-label" for="supportsBatch">
              {{ 'SystemAdmin.supportsBatchProcessing' | translate }}
            </label>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" formControlName="supportsScheduling" id="supportsScheduling">
            <label class="form-check-label" for="supportsScheduling">
              {{ 'SystemAdmin.supportsScheduling' | translate }}
            </label>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" formControlName="isActive" id="isActive">
            <label class="form-check-label" for="isActive">
              {{ 'General.active' | translate }}
            </label>
          </div>
        </div>
      </div>
    </form>
  </div>
  
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="closeModal()">
      {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-primary" (click)="saveIntegrationType()"
            [disabled]="integrationTypeForm.invalid">
      <i class="bi bi-save me-1"></i>
      {{ 'General.save' | translate }}
    </button>
  </div>
</ng-template>

<!-- Delete Confirmation Modal -->
<ng-template #deleteConfirmModal let-modal>
  <div class="modal-header bg-danger text-white">
    <h5 class="modal-title">
      <i class="bi bi-exclamation-triangle me-2"></i>
      {{ 'SystemAdmin.confirmDeleteIntegrationType' | translate }}
    </h5>
    <button type="button" class="btn-close btn-close-white" aria-label="Close" (click)="closeModal()"></button>
  </div>
  
  <div class="modal-body">
    <p>{{ 'SystemAdmin.deleteIntegrationTypeWarning' | translate }}</p>
    <div *ngIf="integrationTypeToDelete" class="alert alert-info">
      <strong>{{ integrationTypeToDelete.typeName }}</strong> ({{ integrationTypeToDelete.typeCode }})
    </div>
    <div *ngIf="deleteUsageCount > 0" class="alert alert-warning">
      <i class="bi bi-exclamation-triangle me-2"></i>
      <strong>{{ 'SystemAdmin.warning' | translate }}:</strong>
      {{ deleteUsageCount }} {{ 'SystemAdmin.activeIntegrationCount' | translate | lowercase }}
    </div>
  </div>
  
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="closeModal()">
      {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-danger" (click)="confirmDelete()">
      <i class="bi bi-trash3 me-1"></i>
      {{ 'General.delete' | translate }}
    </button>
  </div>
</ng-template>
